import { emailWrapper } from './_shared/emailWrapper.js'
import { generateCustomerServiceHtml } from './customerServiceEmails.js'

/**
 * Generate New Order email HTML - Professional print-friendly version.
 * Embeds the shared customer-service section so the recipient gets one
 * fully self-contained HTML document; the PDF variants live in ./orderPdf.js.
 *
 * @param {Object} orderData - Order data
 * @param {Object} orderData.client - Client info (name, makat, address, phone, email)
 * @param {Array} orderData.items - Cart items array
 * @param {Object} orderData.totals - Pricing totals
 * @param {Object} orderData.deposit - Deposit summary
 * @param {number} orderData.orderNumber - Order number (optional)
 * @param {string} catalogUrl - URL to return to catalog
 * @returns {string} Complete HTML email
 */
export function generateNewOrderEmailHtml(orderData, catalogUrl = '', paymentUrl = '') {
  const { client, items, totals, deposit, orderNumber, pointsToAward } = orderData
  const totalItemCount = items.reduce((sum, item) => sum + item.qty, 0)
  const formattedOrderNumber = orderNumber ? String(orderNumber).padStart(7, '0') : null

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '0.00'
    return price.toFixed(2)
  }

  const formatDate = () => {
    return new Date().toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Build items HTML with name row + data row for each product
  const itemsHtml = items
    .map(
      (item, index) => `
    <!-- Product Name Row -->
    <tr style="background-color: ${index % 2 === 0 ? '#f8fafc' : '#f1f5f9'};">
      <td colspan="6" style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0;">
        <div style="font-weight: 600; color: #1e293b; font-size: 14px;">${item.name}</div>
        ${item.isPromoLine ? `<span style="display: inline-block; font-size: 11px; color: #10b981; background: #d1fae5; padding: 2px 8px; border-radius: 10px; margin-top: 4px; margin-left: 6px;">${item.promo?.label || 'מבצע'}</span>` : ''}
        ${item.isDiscountLine ? `<span style="display: inline-block; font-size: 11px; color: #283548; background: #e0e7ff; padding: 2px 8px; border-radius: 10px; margin-top: 4px;">הנחה ${item.discount?.discountPercent}%</span>` : ''}
      </td>
    </tr>
    <!-- Product Data Row -->
    <tr style="background-color: ${index % 2 === 0 ? '#ffffff' : '#fafafa'};">
      <td style="padding: 6px; border-bottom: 2px solid #e2e8f0; vertical-align: middle; text-align: center;">
        ${
          item.image
            ? `<img src="${item.image}" alt="${item.name}" style="width: 40px; height: 40px; object-fit: contain; border-radius: 4px; border: 1px solid #e2e8f0;" />`
            : `<div style="width: 40px; height: 40px; background: #f1f5f9; border-radius: 4px; margin: 0 auto;"></div>`
        }
      </td>
      <td style="padding: 6px; border-bottom: 2px solid #e2e8f0; vertical-align: middle; text-align: right; font-size: 11px; color: #475569; word-break: break-all;">
        ${item.makat}
      </td>
      <td style="padding: 6px; border-bottom: 2px solid #e2e8f0; vertical-align: middle; text-align: right; font-size: 11px; color: #64748b; word-break: break-all;">
        ${item.barcode || '—'}
      </td>
      <td style="padding: 6px; border-bottom: 2px solid #e2e8f0; text-align: center; vertical-align: middle; color: #475569; font-size: 12px; font-weight: 500;">
        ${item.qty}
      </td>
      <td style="padding: 6px; border-bottom: 2px solid #e2e8f0; text-align: left; vertical-align: middle;">
        ${item.isPromoLine || item.isDiscountLine ? `<div style="font-size: 10px; color: #94a3b8; text-decoration: line-through;">${formatPrice(item.catalogPrice)} ₪</div>` : ''}
        <div style="color: #475569; font-size: 11px;">${formatPrice(item.unitPricePreVatPreDeposit)} ₪</div>
      </td>
      <td style="padding: 6px; border-bottom: 2px solid #e2e8f0; text-align: left; vertical-align: middle; font-weight: 600; color: #1e293b; font-size: 12px;">
        ${formatPrice(item.unitPricePreVatPreDeposit * item.qty)} ₪
      </td>
    </tr>
  `
    )
    .join('')

  // Professional template with system colors
  const content = `
    <!-- Document Header -->
    <div style="border-bottom: 2px solid #283548; padding-bottom: 16px; margin-bottom: 24px;">
      <h1 style="margin: 0 0 8px; color: #1e293b; font-size: 22px; font-weight: 700; text-align: center;">
        אישור הזמנה
      </h1>
      ${
        formattedOrderNumber
          ? `
      <p style="margin: 0 0 4px; color: #283548; font-size: 16px; font-weight: 600; text-align: center;">
        מספר הזמנה: ${formattedOrderNumber}
      </p>
      `
          : ''
      }
      <p style="margin: 0; color: #64748b; font-size: 13px; text-align: center;">
        תאריך: ${formatDate()}
      </p>
    </div>

    <!-- Client Details - Compact -->
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom: 24px;">
      <tr>
        <td style="vertical-align: top; width: 50%;">
          <div style="font-weight: 600; color: #1e293b; font-size: 13px; margin-bottom: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">פרטי לקוח</div>
          <div style="font-size: 13px; color: #475569; line-height: 1.6;">
            <div><strong>שם:</strong> ${client.name || '—'}</div>
            ${client.makat ? `<div><strong>מס׳ לקוח:</strong> ${client.makat}</div>` : ''}
            ${client.address ? `<div><strong>כתובת:</strong> ${client.address}</div>` : ''}
          </div>
        </td>
        <td style="vertical-align: top; width: 50%; padding-right: 24px;">
          <div style="font-weight: 600; color: #1e293b; font-size: 13px; margin-bottom: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">פרטי התקשרות</div>
          <div style="font-size: 13px; color: #475569; line-height: 1.6;">
            ${client.phone ? `<div><strong>טלפון:</strong> ${client.phone}</div>` : ''}
            ${client.email ? `<div><strong>אימייל:</strong> ${client.email}</div>` : ''}
          </div>
        </td>
      </tr>
    </table>

    <!-- Order Items Table -->
    <div style="font-weight: 600; color: #1e293b; font-size: 13px; margin-bottom: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">פריטים בהזמנה</div>
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border: 1px solid #e2e8f0; margin-bottom: 24px; table-layout: fixed;">
      <tr style="background: #1e293b;">
        <th style="padding: 8px 6px; text-align: center; font-weight: 600; color: #ffffff; font-size: 10px; border-bottom: 1px solid #e2e8f0; width: 50px;">תמונה</th>
        <th style="padding: 8px 6px; text-align: right; font-weight: 600; color: #ffffff; font-size: 10px; border-bottom: 1px solid #e2e8f0; width: 70px;">מק"ט</th>
        <th style="padding: 8px 6px; text-align: right; font-weight: 600; color: #ffffff; font-size: 10px; border-bottom: 1px solid #e2e8f0; width: 90px;">ברקוד</th>
        <th style="padding: 8px 6px; text-align: center; font-weight: 600; color: #ffffff; font-size: 10px; border-bottom: 1px solid #e2e8f0; width: 45px;">כמות</th>
        <th style="padding: 8px 6px; text-align: left; font-weight: 600; color: #ffffff; font-size: 10px; border-bottom: 1px solid #e2e8f0; width: 70px;">מחיר יח׳</th>
        <th style="padding: 8px 6px; text-align: left; font-weight: 600; color: #ffffff; font-size: 10px; border-bottom: 1px solid #e2e8f0; width: 75px;">סה"כ</th>
      </tr>
      ${itemsHtml}
    </table>

    <!-- Pricing Summary -->
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border: 1px solid #e2e8f0; margin-bottom: 24px;">
      ${
        totals.promoSavingsPreVat > 0
          ? `
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 13px; color: #10b981; border-bottom: 1px solid #f1f5f9;">חיסכון במבצעים</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 13px; color: #10b981; font-weight: 500; border-bottom: 1px solid #f1f5f9; width: 100px;">-${formatPrice(totals.promoSavingsPreVat)} ₪</td>
      </tr>
      `
          : ''
      }
      ${
        totals.discountSavingsPreVat > 0
          ? `
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 13px; color: #283548; border-bottom: 1px solid #f1f5f9;">הנחות כמות</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 13px; color: #283548; font-weight: 500; border-bottom: 1px solid #f1f5f9; width: 100px;">-${formatPrice(totals.discountSavingsPreVat)} ₪</td>
      </tr>
      `
          : ''
      }
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 13px; color: #475569; border-bottom: 1px solid #f1f5f9;">סה"כ פריטים</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 13px; color: #475569; border-bottom: 1px solid #f1f5f9; width: 100px;">${totalItemCount} יח'</td>
      </tr>
      ${
        deposit.hasDeposit
          ? `
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 13px; color: #475569; border-bottom: 1px solid #f1f5f9;">פיקדון (${deposit.depositUnits} יח׳)</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 13px; color: #475569; border-bottom: 1px solid #f1f5f9; width: 100px;">${formatPrice(deposit.depositPreVat)} ₪</td>
      </tr>
      `
          : ''
      }
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 13px; color: #475569; border-bottom: 1px solid #f1f5f9;">סה"כ לפני מע"מ</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 13px; color: #475569; border-bottom: 1px solid #f1f5f9; width: 100px;">${formatPrice(totals.totalPreVat)} ₪</td>
      </tr>
      <tr>
        <td style="padding: 8px 12px; text-align: right; font-size: 13px; color: #64748b; border-bottom: 1px solid #f1f5f9;">מע"מ (18%)</td>
        <td style="padding: 8px 12px; text-align: left; font-size: 13px; color: #64748b; border-bottom: 1px solid #f1f5f9; width: 100px;">${formatPrice(totals.vatAmount)} ₪</td>
      </tr>
      <tr style="background: #f8fafc;">
        <td style="padding: 12px; text-align: right; font-size: 15px; font-weight: 700; color: #1e293b;">סה"כ לתשלום</td>
        <td style="padding: 12px; text-align: left; font-size: 15px; font-weight: 700; color: #283548; width: 100px;">${formatPrice(totals.totalWithVat)} ₪</td>
      </tr>
      ${
        pointsToAward > 0
          ? `
      <tr style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);">
        <td style="padding: 10px 12px; text-align: right; font-size: 13px; font-weight: 600; color: #b45309;">
          ⭐ נקודות שתזכה לקבל
        </td>
        <td style="padding: 10px 12px; text-align: left; font-size: 13px; font-weight: 700; color: #b45309; width: 100px;">${pointsToAward} נקודות</td>
      </tr>
      `
          : ''
      }
    </table>

    ${
      paymentUrl
        ? `
    <!-- Payment CTA -->
    <div style="text-align: center; margin-bottom: 24px;">
      <a href="${paymentUrl}" style="display: inline-block; background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); color: #ffffff; padding: 14px 40px; text-decoration: none; font-weight: 700; font-size: 16px; border-radius: 8px; box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);">
        לחץ לתשלום
      </a>
    </div>
    `
        : ''
    }

    ${generateCustomerServiceHtml(catalogUrl ? new URL(catalogUrl).origin : '')}

    <!-- Footer -->
    <div style="border-top: 1px solid #e2e8f0; padding-top: 16px; text-align: center;">
      <p style="margin: 0 0 12px; color: #64748b; font-size: 12px;">
        תודה על הזמנתך!
      </p>
      ${
        catalogUrl
          ? `
      <a href="${catalogUrl}" style="display: inline-block; background: #283548; color: #ffffff; padding: 10px 24px; text-decoration: none; font-weight: 500; font-size: 13px; border-radius: 6px;">
        חזרה לקטלוג
      </a>
      `
          : ''
      }
    </div>
  `

  const emailTitle = formattedOrderNumber
    ? `אישור הזמנה #${formattedOrderNumber} - ${client.name || 'לקוח'}`
    : `אישור הזמנה - ${client.name || 'לקוח'}`
  return emailWrapper(content, emailTitle)
}
